/**
 * A linked group's consumption queue, in the EXACT order the planner will
 * walk it (shared list-order helpers — the engine uses the same rule).
 * MANUAL makes the rows draggable (not while filtered); FORMULA (Phase 18)
 * shows what the lists are ranked on — choosing Formula, or clicking that
 * line, opens the formula and its rules in the Flow Optimizer's window, the
 * queue live on the right (user, 12/10/2026: "it should open
 * automatically") — and each row's score, or why it is not ranked.
 * 02/11/2026 (user: "really messy and not readable"): a "List order ⓘ"
 * header like the section above it (the order's explanation behind the ⓘ,
 * the ranking's state on the right), and the rows mark Today and Next.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, useMemo } from 'react';
import { Maximize2 } from 'lucide-react';
import { compareListsByDefaultOrder, describeHourFormula } from '@trafficpulse/shared';

import { HintDot } from '@/components/ui';
import { cn } from '@/lib/utils';
import { useListGroupResolve } from '@/features/list-groups';

import type { DraftGroupRef } from '../../../drop-generator.types';
import { rulesSummary } from '../rules/rule-model';
import type { ContainerEditor } from '../state/useContainerEditor';
import { ListFormulaDialog, rankingShort, rankingStatus } from './formula/ListFormulaDialog';
import { queueMarks, queueOrder } from './formula/queue-order';
import { useListRanking } from './formula/use-list-ranking';
import type { GroupDay } from './lists-per-day/group-days';
import { QueueList } from './queue/QueueList';

const LABEL = 'text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground';

const STAYS = 'During the day the group keeps its list until a verdict moves it on; this order decides where it goes next.';
const CAPTION: Record<string, string> = {
  MANUAL: `Your order: drag rows to change it. New rule matches join at the back. ${STAYS}`,
  MANUAL_UNSET: `Biggest list first until you drag a row into your own order; then new rule matches join at the back. ${STAYS}`,
  FORMULA: "Best score first; lists with too little traffic follow, biggest first. Generate now and the daily auto-generate start on the formula's #1 (burned lists stay out); during the day the group keeps its list.",
  INHERIT: `Biggest list first; depleted lists are skipped. ${STAYS}`,
};

/** "My order" with nothing dragged yet still reads biggest first. */
const captionKey = (link: DraftGroupRef) =>
  link.orderMode === 'MANUAL' && !link.manualOrderJson?.length ? 'MANUAL_UNSET' : link.orderMode;

interface Props {
  link: DraftGroupRef;
  group: { name: string; color: string };
  editor: ContainerEditor;
  formulaSource: { countryKey: string; campaignWiserId: number | null };
  onPatch: (patch: Partial<DraftGroupRef>) => void;
  /** The list formula window — opened by choosing Formula (GroupsTab) or clicking "Ranked on". */
  formulaOpen: boolean;
  onFormulaOpenChange: (open: boolean) => void;
  /** Today's lists of this group (the Today / Next marks) and whether it sends to one list a day. */
  day?: Pick<GroupDay, 'ids' | 'out'>;
  oneList?: boolean;
}

export const GroupOrderPanel: FC<Props> = ({ link, group, editor, formulaSource, onPatch, formulaOpen, onFormulaOpenChange, day, oneList = true }) => {
  const resolve = useListGroupResolve(link.listGroupId);
  const formula = link.orderMode === 'FORMULA';
  const manual = link.orderMode === 'MANUAL';
  const byDefault = useMemo(() => [...(resolve.data?.lists ?? [])].sort(compareListsByDefaultOrder), [resolve.data]);
  const ranking = useListRanking({ ...formulaSource, ranking: link.orderFormulaJson, wiserIds: byDefault.map((r) => r.wiserId), enabled: formula });
  const preview = formula ? ranking.data : undefined;
  const ordered = useMemo(() => queueOrder(byDefault, link, preview), [byDefault, link, preview]);
  const marks = useMemo(() => queueMarks(ordered, preview, day, oneList), [ordered, preview, day, oneList]);

  if (resolve.isLoading) return <div className="mx-3 mb-2 h-16 animate-pulse rounded-lg bg-muted/60" />;
  if (resolve.isError)
    return (
      <p className="mx-3 mb-2 rounded-lg border border-dashed border-border px-3 py-2 text-xs text-muted-foreground">
        Queue preview unavailable.{' '}
        <button type="button" className="font-medium text-primary hover:underline" onClick={() => resolve.refetch()}>
          Retry
        </button>
      </p>
    );

  return (
    <section className="flex flex-col gap-1.5 border-t border-border/50 px-3 py-2" data-testid="group-order">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h4 className={cn(LABEL, 'inline-flex items-center gap-1.5')}>
          List order
          <HintDot label={CAPTION[captionKey(link)] ?? CAPTION.INHERIT} />
        </h4>
        <span className={cn('text-xs', formula && ranking.isError ? 'font-medium text-warning' : 'text-muted-foreground')}>
          {formula ? rankingShort(ranking) : manual ? 'Drag to reorder' : null}
        </span>
      </div>
      {formula ? (
        <button
          type="button"
          aria-haspopup="dialog"
          aria-label="Edit the list formula"
          onClick={() => onFormulaOpenChange(true)}
          className="group flex w-full items-center gap-2 rounded-md border border-border/60 bg-card px-2.5 py-1.5 text-left text-xs text-muted-foreground transition-shadow hover:border-primary/40 hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <span className="min-w-0 flex-1 truncate">
            Ranked on <span className="text-foreground">{[describeHourFormula(link.orderFormulaJson.formula), rulesSummary(link.orderFormulaJson.rules)].filter(Boolean).join(' · ')}</span>
          </span>
          <Maximize2 className="h-3.5 w-3.5 shrink-0 transition-colors group-hover:text-primary" aria-hidden />
        </button>
      ) : null}
      {formula && ranking.isError ? <p className="text-[11px] text-warning">{rankingStatus(ranking)}</p> : null}
      <QueueList ordered={ordered} preview={preview} manual={manual} marks={marks} onReorder={(manualOrderJson) => onPatch({ manualOrderJson })} />
      {formula ? (
        <ListFormulaDialog
          open={formulaOpen}
          onOpenChange={onFormulaOpenChange}
          editor={editor}
          group={group}
          ranking={link.orderFormulaJson}
          query={ranking}
          ordered={ordered}
          preview={preview}
          onChange={(r) => onPatch({ orderFormulaJson: r })}
        />
      ) : null}
    </section>
  );
};
